import {MarkdownEditor} from '@github-ui/markdown-editor'
import {testIdProps} from '@github-ui/test-id-props'
import {CalendarIcon, RocketIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {MarkdownEditor as PrimerMarkdownEditor} from '@primer/react/experimental'
import {useCallback, useReducer} from 'react'

import type {PersistedOption} from '../../api/columns/contracts/single-select'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {type StatusUpdate, useStatusUpdates} from '../../state-providers/status-updates/status-updates-context'
import {StatusUpdatesResources} from '../../strings'
import {StatusPicker} from './status-picker'
import {StatusUpdateDatePicker} from './status-update-date-picker'

type StatusUpdateProps = {
  onCancel: () => void
  onSave: (statusUpdate: StatusUpdate) => void
  statusUpdate?: StatusUpdate
}

export const StatusUpdateActionTypes = {
  UPDATE_STATUS: 'UPDATE_STATUS',
  UPDATE_BODY: 'UPDATE_BODY',
  UPDATE_START_DATE: 'UPDATE_START_DATE',
  UPDATE_TARGET_DATE: 'UPDATE_TARGET_DATE',
} as const

type StatusUpdateAction =
  | {
      type: typeof StatusUpdateActionTypes.UPDATE_BODY
      payload: string
    }
  | {
      type: typeof StatusUpdateActionTypes.UPDATE_STATUS
      payload: PersistedOption | null
    }
  | {
      type: typeof StatusUpdateActionTypes.UPDATE_START_DATE | typeof StatusUpdateActionTypes.UPDATE_TARGET_DATE
      payload: Date | null
    }

const statusUpdateReducer = (state: StatusUpdate, action: StatusUpdateAction): StatusUpdate => {
  switch (action.type) {
    case StatusUpdateActionTypes.UPDATE_BODY: {
      return {...state, body: action.payload}
    }
    case StatusUpdateActionTypes.UPDATE_START_DATE: {
      return {...state, startDate: action.payload}
    }
    case StatusUpdateActionTypes.UPDATE_STATUS: {
      return {...state, status: action.payload}
    }
    case StatusUpdateActionTypes.UPDATE_TARGET_DATE: {
      return {...state, targetDate: action.payload}
    }
  }
}

export const StatusUpdateEditor = ({onCancel, onSave, statusUpdate}: StatusUpdateProps) => {
  const {statusOptions} = useStatusUpdates()

  const [localStatusUpdate, dispatch] = useReducer(
    statusUpdateReducer,
    statusUpdate ?? {
      body: '',
      status: null,
      startDate: null,
      targetDate: null,
    },
  )

  const {hasWritePermissions} = ViewerPrivileges()

  const handleSave = useCallback(() => {
    if (!hasWritePermissions) return

    onSave(localStatusUpdate)
  }, [hasWritePermissions, localStatusUpdate, onSave])

  const handleCancel = useCallback(() => {
    onCancel()
  }, [onCancel])

  const handleBodyChanged = useCallback((body: StatusUpdate['body']) => {
    dispatch({type: StatusUpdateActionTypes.UPDATE_BODY, payload: body})
  }, [])

  const handleStatusChanged = useCallback((status: StatusUpdate['status']) => {
    dispatch({type: StatusUpdateActionTypes.UPDATE_STATUS, payload: status})
  }, [])

  const handleStartDateChanged = useCallback((startDate: StatusUpdate['startDate']) => {
    dispatch({type: StatusUpdateActionTypes.UPDATE_START_DATE, payload: startDate})
  }, [])

  const handleTargetDateChanged = useCallback((targetDate: StatusUpdate['targetDate']) => {
    dispatch({type: StatusUpdateActionTypes.UPDATE_TARGET_DATE, payload: targetDate})
  }, [])

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
        <StatusPicker defaultStatus={localStatusUpdate.status} onChange={handleStatusChanged} options={statusOptions} />
        <StatusUpdateDatePicker
          ariaLabel={StatusUpdatesResources.startDatePickerAriaLabel}
          defaultDate={localStatusUpdate.startDate}
          icon={CalendarIcon}
          label={StatusUpdatesResources.startDateFieldLabel}
          onChange={handleStartDateChanged}
          testId="status-updates-start-date-picker"
        />
        <StatusUpdateDatePicker
          ariaLabel={StatusUpdatesResources.targetDatePickerAriaLabel}
          defaultDate={localStatusUpdate.targetDate}
          icon={RocketIcon}
          label={StatusUpdatesResources.targetDateFieldLabel}
          onChange={handleTargetDateChanged}
          testId="status-updates-target-date-picker"
        />
      </Box>
      <Box sx={{width: '100%'}} {...testIdProps('status-updates-markdown-editor')}>
        <MarkdownEditor value={localStatusUpdate.body} onChange={handleBodyChanged} />
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
        <PrimerMarkdownEditor.ActionButton
          size="medium"
          onClick={handleCancel}
          {...testIdProps('status-update-cancel-button')}
        >
          {StatusUpdatesResources.cancelUpdateButtonText}
        </PrimerMarkdownEditor.ActionButton>
        <PrimerMarkdownEditor.ActionButton
          size="medium"
          variant="primary"
          onClick={handleSave}
          {...testIdProps('status-update-save-button')}
        >
          {StatusUpdatesResources.saveUpdateButtonText}
        </PrimerMarkdownEditor.ActionButton>
      </Box>
    </Box>
  )
}

try{ StatusUpdateEditor.displayName ||= 'StatusUpdateEditor' } catch {}